import { IItemForGetShortInfo, UserRoles } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import DraggableModifierGroup from '../DraggableModifierGroup';
import { IModifierGroupWithAdditionalData } from 'modules/Menu/Dishes/EditDish/editDishState/editDishContext';
import React from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import style from './DnDList.module.scss';

interface IDnDList {
	setModifierGroups: (value: string[]) => void;
	selectedModifierGroups: string[];
	fetchedModifierGroups: IItemForGetShortInfo[];
	changeLowerLimit: (id: string, value: string) => void;
	changeUpperLimit: (id: string, value: string) => void;
	toggleLimitsProtectedFromSync: (id: string, value: boolean) => void;
	modifierGroupsWithAdditionalData: IModifierGroupWithAdditionalData[];
}

const DnDList: React.FC<IDnDList> = ({
	selectedModifierGroups,
	fetchedModifierGroups,
	setModifierGroups,
	changeUpperLimit,
	changeLowerLimit,
	toggleLimitsProtectedFromSync,
	modifierGroupsWithAdditionalData,
}) => {
	const hasChangePositionAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const handleDragEnd = (result: DropResult) => {
		if (!modifierGroupsWithAdditionalData.length || !result.destination) {
			return null;
		}
		const items = modifierGroupsWithAdditionalData;
		const [reorderedItem] = items.splice(result.source.index, 1);
		items.splice(result.destination.index, 0, reorderedItem);
		setModifierGroups(items.map((el) => el.modifierGroupId));
	};

	const handleRemove = (id: string) => {
		const updatedRestaurants = selectedModifierGroups.filter((el) => el !== id);
		setModifierGroups(updatedRestaurants);
	};

	return (
		<DragDropContext onDragEnd={handleDragEnd}>
			<Droppable droppableId="modifierGroupList">
				{(provided) => (
					<div className={style.groupsList} ref={provided.innerRef} {...provided.droppableProps}>
						{modifierGroupsWithAdditionalData.map((el, index) => {
							const hasNextItem = !!selectedModifierGroups[index + 1];

							return (
								<React.Fragment key={el.modifierGroupId}>
									<Draggable draggableId={el.modifierGroupId} index={index}>
										{(provided) => {
											const label =
												fetchedModifierGroups.find(
													(fetched) => fetched.id === el.modifierGroupId,
												)?.name || '';
											return (
												<DraggableModifierGroup
													draggableInnerRef={provided.innerRef}
													provided={provided}
													positionNumber={index + 1}
													label={label}
													onDelete={() => handleRemove(el.modifierGroupId)}
													hasDragAccess={hasChangePositionAccess}
													changeLowerLimit={changeLowerLimit}
													changeUpperLimit={changeUpperLimit}
													toggleLimitsProtectedFromSync={toggleLimitsProtectedFromSync}
													modifierGroupId={el.modifierGroupId}
													isLimitsProtectedFromSync={el.isLimitsProtectedFromSync}
												/>
											);
										}}
									</Draggable>
									{hasNextItem && <div className={style.divider} />}
								</React.Fragment>
							);
						})}
						{provided.placeholder}
					</div>
				)}
			</Droppable>
		</DragDropContext>
	);
};

export default React.memo(DnDList);
